import React, { useState } from 'react';
import { usePartner } from '../../context/PartnerContext';

const AddAccountModal = ({ isOpen, onClose, onAccountAdded }) => {
  const { appName } = usePartner();
  const [tab, setTab]       = useState('existing');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [form, setForm]     = useState({
    account_number: '',
    broker: '',
    platform: 'MT5',
    server: '',
    investor_password: '',
    nickname: '',
    about: '',
    account_role: 'trader',
    account_type: 'standard',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.nickname || !form.nickname.trim()) {
      setError('Strategy name / nickname is required — investors will see this on the marketplace.');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const res = await fetch('/api/trader/accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, currency: form.account_type === 'cent' ? 'USC' : 'USD', is_cent: form.account_type === 'cent' }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        onClose();
        if (onAccountAdded) onAccountAdded(data.account);
        window.location.reload();
      } else {
        setError(data.error || 'Failed to register account. Please check your details.');
      }
    } catch {
      setError('Connection failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const S = {
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: '16px',
    },
    modal: {
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: 520,
      maxHeight: '90vh', overflowY: 'auto',
      boxShadow: 'var(--shadow-md)',
    },
    header: {
      padding: '24px 24px 0',
    },
    title: {
      fontSize: 18, fontWeight: 700, color: 'var(--text-main)', marginBottom: 4,
    },
    subtitle: {
      fontSize: 13, color: 'var(--text-muted)', marginBottom: 20,
    },
    tabs: {
      display: 'flex', borderBottom: '1px solid var(--border)',
      padding: '0 24px',
    },
    tab: (active) => ({
      padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
      background: 'transparent', border: 'none',
      color: active ? 'var(--accent)' : 'var(--text-muted)',
      borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
      transition: 'all var(--t)',
    }),
    body: { padding: '20px 24px 24px' },
    label: {
      display: 'block', fontSize: 11, fontWeight: 600,
      color: 'var(--text-muted)', letterSpacing: '0.06em',
      textTransform: 'uppercase', marginBottom: 6,
    },
    input: {
      width: '100%', padding: '10px 14px',
      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)', color: 'var(--text-main)',
      fontSize: 13, outline: 'none', boxSizing: 'border-box',
      transition: 'border-color var(--t)',
    },
    hint: { fontSize: 11, color: 'var(--text-faint)', marginTop: 4 },
    field: { marginBottom: 16 },
    row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 },
    platformBtn: (active) => ({
      padding: '10px', border: '1px solid',
      borderColor: active ? 'var(--accent)' : 'var(--border)',
      borderRadius: 'var(--radius-md)',
      background: active ? 'var(--accent-soft)' : 'transparent',
      color: active ? 'var(--accent)' : 'var(--text-muted)',
      fontSize: 13, fontWeight: 600, cursor: 'pointer',
      transition: 'all var(--t)', textAlign: 'center',
    }),
    error: {
      padding: '10px 14px', background: 'var(--danger-soft)',
      border: '1px solid rgba(239,68,68,0.35)', borderRadius: 'var(--radius-md)',
      color: 'var(--danger)', fontSize: 12, marginBottom: 16,
    },
    footer: {
      display: 'flex', gap: 10, marginTop: 20,
    },
    btnSecondary: {
      flex: 1, padding: '10px 0', background: 'transparent',
      border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
      color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    btnPrimary: {
      flex: 2, padding: '10px 0', background: 'var(--accent)',
      border: 'none', borderRadius: 'var(--radius-md)',
      color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    infoBox: {
      padding: '12px 14px', background: 'var(--accent-soft)',
      border: '1px solid var(--accent-border)', borderRadius: 'var(--radius-md)',
      fontSize: 12, color: 'var(--accent)', marginTop: 16,
    },
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={S.header}>
          <div style={S.title}>Add Trading Account</div>
          <div style={S.subtitle}>Connect an existing account or open a new one</div>
        </div>

        {/* Tabs */}
        <div style={S.tabs}>
          <button style={S.tab(tab==='existing')} onClick={()=>setTab('existing')}>
            Connect Existing
          </button>
          <button style={S.tab(tab==='new')} onClick={()=>setTab('new')}>
            Open New Account
          </button>
        </div>

        <div style={S.body}>
          {tab === 'existing' ? (
            <form onSubmit={handleSubmit}>

              {/* Platform */}
              <div style={S.field}>
                <label style={S.label}>Platform</label>
                <div style={S.row2}>
                  {['MT4','MT5'].map(p => (
                    <button key={p} type="button"
                      style={S.platformBtn(form.platform===p)}
                      onClick={()=>set('platform',p)}>{p}</button>
                  ))}
                </div>
              </div>

              {/* Account Type */}
              <div style={S.field}>
                <label style={S.label}>Account Type</label>
                <div style={S.row2}>
                  {[['standard','Standard (USD)'],['cent','Cent (USC)']].map(([v,l]) => (
                    <button key={v} type="button"
                      style={S.platformBtn(form.account_type===v)}
                      onClick={()=>set('account_type',v)}>{l}</button>
                  ))}
                </div>
                <div style={S.hint}>Cent accounts: values shown in USC (1 USD = 100 USC)</div>
              </div>
              {/* Account Number */}
              <div style={S.field}>
                <label style={S.label}>Account Number</label>
                <input required style={S.input}
                  placeholder="e.g. 2055822"
                  value={form.account_number}
                  onChange={e=>set('account_number',e.target.value)}/>
              </div>

              {/* Server */}
              <div style={S.field}>
                <label style={S.label}>Server Name</label>
                <input required style={S.input}
                  placeholder="e.g. Dupoin-Real, FBS-Real5"
                  value={form.server}
                  onChange={e=>set('server',e.target.value)}/>
                <div style={S.hint}>Case-sensitive — check your broker's server name</div>
              </div>

              {/* Broker */}
              <div style={S.field}>
                <label style={S.label}>Broker</label>
                <input required style={S.input}
                  placeholder="e.g. Exness, FBS, Dupoin"
                  value={form.broker}
                  onChange={e=>set('broker',e.target.value)}/>
              </div>

              {/* Investor Password */}
              <div style={S.field}>
                <label style={S.label}>Investor Password</label>
                <input required type="password" style={S.input}
                  placeholder="Read-only investor password"
                  value={form.investor_password}
                  onChange={e=>set('investor_password',e.target.value)}/>
                <div style={S.hint}>This is the read-only password, NOT your trading password</div>
              </div>

              {/* Nickname */}
              <div style={S.field}>
                <label style={S.label}>Nickname <span style={{color:'var(--danger)',fontWeight:400}}>*required</span></label>
                <input style={S.input} required
                  placeholder="e.g. Gold Swing, Conservative"
                  value={form.nickname}
                  onChange={e=>set('nickname',e.target.value)}/>
                <div style={S.hint}>This is how investors will see you on the marketplace.</div>
              </div>

              {/* About */}
              <div style={S.field}>
                <label style={S.label}>Strategy Description <span style={{color:'var(--text-faint)',fontWeight:400}}>(optional)</span></label>
                <textarea rows={3} style={{...S.input, resize:'vertical', fontFamily:'inherit'}}
                  placeholder="Describe your trading strategy — shown in Marketplace"
                  value={form.about}
                  onChange={e=>set('about',e.target.value)}/>
              </div>

              {error && <div style={S.error}>{error}</div>}

              <div style={S.footer}>
                <button type="button" style={S.btnSecondary} onClick={onClose}>Cancel</button>
                <button type="submit" style={{...S.btnPrimary, opacity:loading?0.6:1}} disabled={loading}>
                  {loading ? 'Adding...' : 'Add Account'}
                </button>
              </div>

              <div style={S.infoBox}>
                Next step: connect your EA to start syncing trades automatically.
              </div>
            </form>
          ) : (
            <div style={{textAlign:'center', padding:'32px 0'}}>
              <div style={{width:56,height:56,borderRadius:'var(--radius-lg)',
                background:'var(--accent-soft)',border:'1px solid var(--accent-border)',
                display:'flex',alignItems:'center',justifyContent:'center',
                margin:'0 auto 16px',fontSize:24}}>
                
              </div>
              <div style={{fontSize:16,fontWeight:700,color:'var(--text-main)',marginBottom:8}}>
                Open New Broker Account
              </div>
              <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:24,lineHeight:1.6}}>
                Open a new MT4/MT5 account with our partner brokers and start trading on {appName || "CrunchAlpha"}.
              </div>
              <div style={{background:'var(--bg-elevated)',border:'1px solid var(--border)',
                borderRadius:'var(--radius-md)',padding:'16px',marginBottom:24,textAlign:'left'}}>
                {[
                  'Trusted and regulated partner brokers',
                  'Full MT4 and MT5 support',
                  'Competitive spreads',
                ].map((t,i)=>(
                  <div key={i} style={{display:'flex',gap:10,alignItems:'center',
                    marginBottom: i<2?10:0, fontSize:13,color:'var(--text-muted)'}}>
                    <div style={{width:6,height:6,borderRadius:'50%',
                      background:'var(--success)',flexShrink:0}}/>
                    {t}
                  </div>
                ))}
              </div>
              <button
                onClick={()=>window.location.href='/brokers'}
                style={{...S.btnPrimary, flex:'none', padding:'10px 32px', display:'inline-block'}}>
                View Partner Brokers
              </button>
              <div style={{marginTop:16,fontSize:12,color:'var(--text-faint)'}}>
                After opening an account, return here and use "Connect Existing"
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddAccountModal;
